/**
 * Settings → Redemption Locator (admins/superadmins). Edits the single voucher_locator_settings row
 * that voucher_locator_listed_organizations() — the ONE listing rule — reads, and shows the live
 * count it produces. RLS and the admin RPCs refuse everyone else server-side; hiding the tab is only
 * convenience.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getVoucherLocatorList,
  getVoucherLocatorSettings,
  getVoucherLocatorSummary,
  saveVoucherLocatorSettings,
} from "../../services/voucherLocator.service";

const label = (t: string) =>
  t
    ? t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "(no type)";

function toCsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) {
    return "";
  }
  const cols = Object.keys(rows[0]);
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [
    cols.join(","),
    ...rows.map((r) => cols.map((c) => esc(r[c])).join(",")),
  ].join("\n");
}

const VoucherLocatorSettings: React.FC = () => {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ["voucher-locator-settings"],
    queryFn: getVoucherLocatorSettings,
  });
  const summary = useQuery({
    queryKey: ["voucher-locator-summary"],
    queryFn: getVoucherLocatorSummary,
  });

  const [included, setIncluded] = useState<string[]>([]);
  const [requireCta, setRequireCta] = useState(true);
  const [conceal, setConceal] = useState<string[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data) {
      setIncluded(settings.data.included_org_types);
      setRequireCta(settings.data.require_active_voucher_cta);
      setConceal(settings.data.conceal_street_address_types);
    }
  }, [settings.data]);

  // Every type present in the data, plus any configured type with no organizations yet.
  const types = useMemo(() => {
    const rows = summary.data?.by_type ?? [];
    const known = new Set(rows.map((r) => r.type));
    const extra = (settings.data?.included_org_types ?? []).filter(
      (t) => !known.has(t),
    );
    return [
      ...rows,
      ...extra.map((t) => ({
        type: t,
        included: true,
        listed: 0,
        no_pin: 0,
        organizations: 0,
      })),
    ];
  }, [summary.data, settings.data]);

  const dirty =
    !!settings.data &&
    (JSON.stringify([...included].sort()) !==
      JSON.stringify([...settings.data.included_org_types].sort()) ||
      requireCta !== settings.data.require_active_voucher_cta ||
      JSON.stringify([...conceal].sort()) !==
        JSON.stringify([...settings.data.conceal_street_address_types].sort()));

  const save = useMutation({
    mutationFn: () =>
      saveVoucherLocatorSettings({
        included_org_types: included,
        require_active_voucher_cta: requireCta,
        conceal_street_address_types: conceal,
      }),
    onSuccess: async () => {
      setNotice("Saved. The public locator uses these settings immediately.");
      await qc.invalidateQueries({ queryKey: ["voucher-locator-settings"] });
      await qc.invalidateQueries({ queryKey: ["voucher-locator-summary"] });
    },
    onError: (e: Error) => setNotice(e.message),
  });

  const toggle = (list: string[], set: (v: string[]) => void, t: string) =>
    set(list.includes(t) ? list.filter((x) => x !== t) : [...list, t]);

  const download = async () => {
    setDownloading(true);
    try {
      const rows = await getVoucherLocatorList();
      const blob = new Blob(
        [toCsv(rows as unknown as Record<string, unknown>[])],
        { type: "text/csv" },
      );
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `redemption-locator-partners-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setDownloading(false);
    }
  };

  if (settings.isLoading || summary.isLoading) {
    return <p className="text-gray-600">Loading…</p>;
  }
  if (settings.isError || summary.isError) {
    return (
      <p role="alert" className="text-red-700">
        Unable to load the Redemption Locator settings. Only admins can view
        them.
      </p>
    );
  }

  return (
    <div className="space-y-6" data-testid="voucher-locator-settings">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Redemption Locator
          </h2>
          <p className="mt-1 text-sm text-gray-600 max-w-2xl">
            Controls which partners appear on the public &ldquo;Redeem your
            voucher&rdquo; page. A voucher link only ever shows the listed
            partners that can redeem that specific voucher.
          </p>
        </div>
        <a
          href="/voucher-locations"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-700 underline whitespace-nowrap"
        >
          Open public page
        </a>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <p
          className="text-2xl font-semibold text-gray-900"
          data-testid="voucher-locator-listed-total"
        >
          {summary.data?.listed_total ?? 0} partners on the map
        </p>
        <p className="text-sm text-gray-700">Based on the saved settings.</p>
        <button
          type="button"
          onClick={download}
          disabled={downloading}
          className="mt-2 text-sm text-blue-700 underline disabled:opacity-50"
        >
          {downloading ? "Preparing…" : "Download the list (CSV)"}
        </button>
      </div>

      <div>
        <h3 className="font-medium text-gray-900">Partner types shown</h3>
        <p className="text-sm text-gray-600 mb-2">
          Only checked types appear. New types stay hidden until someone checks
          them here.
        </p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1">Show</th>
              <th>Type</th>
              <th>Organizations</th>
              <th>On the map now</th>
              <th>No map location</th>
              <th>Hide street address</th>
            </tr>
          </thead>
          <tbody>
            {types.map((t) => (
              <tr key={t.type || "(none)"} className="border-t">
                <td className="py-1">
                  <input
                    type="checkbox"
                    aria-label={`Show ${label(t.type)}`}
                    checked={included.includes(t.type)}
                    disabled={!t.type}
                    onChange={() => toggle(included, setIncluded, t.type)}
                  />
                </td>
                <td>{label(t.type)}</td>
                <td>{t.organizations}</td>
                <td>{t.listed}</td>
                <td>{t.no_pin}</td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Hide street address for ${label(t.type)}`}
                    checked={conceal.includes(t.type)}
                    disabled={!t.type}
                    onChange={() => toggle(conceal, setConceal, t.type)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-gray-500 mt-2">
          &ldquo;Hide street address&rdquo; shows only city, state and ZIP, with
          an approximate pin. It is on for instructors by default because many
          are individuals whose address on file may be a home.
        </p>
      </div>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={requireCta}
          onChange={(e) => setRequireCta(e.target.checked)}
        />
        <span>
          <strong>Only partners with an active voucher CTA.</strong> Hides
          partners whose voucher CTA is inactive or outside its date range.
        </span>
      </label>

      <div className="flex items-center gap-3">
        <button
          type="button"
          className="bg-blue-700 text-white rounded px-4 py-2 disabled:opacity-50"
          disabled={!dirty || save.isPending}
          onClick={() => {
            setNotice(null);
            save.mutate();
          }}
        >
          {save.isPending ? "Saving…" : "Save changes"}
        </button>
        {notice && (
          <span role="status" className="text-sm text-gray-700">
            {notice}
          </span>
        )}
      </div>
    </div>
  );
};

export default VoucherLocatorSettings;
